import { ThreadManagement } from "./orchestration.ts";
import { ProviderGoalSnapshot } from "./providerGoal.ts";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import {
  CommandId,
  EnvironmentId,
  EventId,
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
} from "./baseSchemas.ts";
import {
  ModelSelection,
  EnvironmentCommandAcceptedWorkIdentity,
  EnvironmentCommandNormalizedInputDigest,
  OrchestrationThread,
  OrchestrationThreadActivityTone,
  OrchestrationThreadShell,
  ProviderApprovalDecision,
  ProviderRequestKind,
} from "./orchestration.ts";
import { ProviderDriverKind, ProviderInstanceId } from "./providerInstance.ts";
import { ThreadContextBinding } from "./provider.ts";
import {
  ServerProviderAuth,
  ServerProviderAvailability,
  ServerProviderModel,
  ServerProviderReadiness,
  ServerProviderState,
} from "./server.ts";
import {
  AvailableProjectWorktree,
  ManagedThreadWorktree,
  ManagedThreadWorktreeId,
  ThreadWorktreeLocation,
} from "./worktrees.ts";

export const COMMAND_LINE_DIAGNOSTIC_MAX_CHARS = 512;

export const CommandLineProjectIdentity = Schema.Struct({
  type: Schema.Literal("afar-project"),
  projectId: ProjectId,
});
export type CommandLineProjectIdentity = typeof CommandLineProjectIdentity.Type;

export const CommandLineProjectSummary = Schema.Struct({
  identity: CommandLineProjectIdentity,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
});
export type CommandLineProjectSummary = typeof CommandLineProjectSummary.Type;

export const CommandLineProjectListData = Schema.Struct({
  type: Schema.Literal("project-list"),
  projects: Schema.Array(CommandLineProjectSummary),
});
export type CommandLineProjectListData = typeof CommandLineProjectListData.Type;

export const CommandLineProjectShowData = Schema.Struct({
  type: Schema.Literal("project-show"),
  project: CommandLineProjectSummary,
  resolvedBy: Schema.Literals(["project-id", "explicit-path", "checkout-inference"]),
});
export type CommandLineProjectShowData = typeof CommandLineProjectShowData.Type;

export const CommandLineProjectCommand = Schema.Literals(["project.list", "project.show"]);
export type CommandLineProjectCommand = typeof CommandLineProjectCommand.Type;

export const CommandLineProjectFailureCode = Schema.Literals([
  "invalid-input",
  "project-not-found",
  "project-ambiguous",
  "environment-stopped",
  "environment-unavailable",
  "operational-failure",
]);
export type CommandLineProjectFailureCode = typeof CommandLineProjectFailureCode.Type;

const CommandLineSafeDiagnostic = TrimmedNonEmptyString.check(
  Schema.isMaxLength(COMMAND_LINE_DIAGNOSTIC_MAX_CHARS),
);

export const CommandLineProjectError = Schema.Struct({
  code: CommandLineProjectFailureCode,
  message: CommandLineSafeDiagnostic,
});
export type CommandLineProjectError = typeof CommandLineProjectError.Type;

export const CommandLineEnvironmentIdentity = Schema.Struct({
  environmentId: EnvironmentId,
  lifecycleGeneration: TrimmedNonEmptyString,
});
export type CommandLineEnvironmentIdentity = typeof CommandLineEnvironmentIdentity.Type;

export const CommandLineApplicationReleaseIdentity = Schema.Struct({
  client: TrimmedNonEmptyString,
  environment: Schema.NullOr(TrimmedNonEmptyString),
});
export type CommandLineApplicationReleaseIdentity =
  typeof CommandLineApplicationReleaseIdentity.Type;

const CommandLineProjectResultMetadata = {
  recordType: Schema.Literal("project-command-result"),
  command: CommandLineProjectCommand,
  applicationRelease: CommandLineApplicationReleaseIdentity,
} as const;

export const CommandLineProjectSuccessResult = Schema.Struct({
  ...CommandLineProjectResultMetadata,
  outcome: Schema.Literal("success"),
  data: Schema.Union([CommandLineProjectListData, CommandLineProjectShowData]),
  error: Schema.Null,
  environment: CommandLineEnvironmentIdentity,
});
export type CommandLineProjectSuccessResult = typeof CommandLineProjectSuccessResult.Type;

export const CommandLineProjectFailureResult = Schema.Struct({
  ...CommandLineProjectResultMetadata,
  outcome: CommandLineProjectFailureCode,
  data: Schema.Null,
  error: CommandLineProjectError,
  environment: Schema.NullOr(CommandLineEnvironmentIdentity),
});
export type CommandLineProjectFailureResult = typeof CommandLineProjectFailureResult.Type;

export const CommandLineProjectResult = Schema.Union([
  CommandLineProjectSuccessResult,
  CommandLineProjectFailureResult,
]);
export type CommandLineProjectResult = typeof CommandLineProjectResult.Type;

export const COMMAND_LINE_UNARY_LIST_MAX_RECORDS = 1_000;
export const COMMAND_LINE_PROVIDER_FIELD_MAX_BYTES = 1024 * 1024;
export const COMMAND_LINE_UNARY_RESULT_MAX_BYTES = 16 * 1024 * 1024;

export const CommandLineDiscoveryProject = Schema.Struct({
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  authoritativeRevision: Schema.optional(IsoDateTime),
  /** Only Brain threads can be created until its Project Study starts or is skipped. */
  awaitsProjectStudy: Schema.optional(Schema.Boolean),
});
export type CommandLineDiscoveryProject = typeof CommandLineDiscoveryProject.Type;

export const CommandLineProjectWorktreeInventory = Schema.Union([
  Schema.Struct({
    projectId: ProjectId,
    status: Schema.Literal("available"),
    isGitRepository: Schema.Boolean,
    unavailableReason: Schema.NullOr(TrimmedNonEmptyString),
    worktrees: Schema.Array(AvailableProjectWorktree),
  }),
  Schema.Struct({
    projectId: ProjectId,
    status: Schema.Literal("unavailable"),
    unavailableReason: TrimmedNonEmptyString,
    worktrees: Schema.Tuple([]),
  }),
]);
export type CommandLineProjectWorktreeInventory = typeof CommandLineProjectWorktreeInventory.Type;

export const CommandLineDiscoveryProvider = Schema.Struct({
  instanceId: ProviderInstanceId,
  driver: ProviderDriverKind,
  displayName: Schema.optional(TrimmedNonEmptyString),
  enabled: Schema.Boolean,
  installed: Schema.Boolean,
  status: ServerProviderState,
  readiness: Schema.optional(ServerProviderReadiness),
  auth: ServerProviderAuth,
  availability: Schema.optional(ServerProviderAvailability),
  models: Schema.Array(ServerProviderModel),
});
export type CommandLineDiscoveryProvider = typeof CommandLineDiscoveryProvider.Type;

export const CommandLineDiscoverySnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt.pipe(Schema.withDecodingDefault(Effect.succeed(0))),
  projects: Schema.Array(CommandLineDiscoveryProject),
  inventories: Schema.Array(CommandLineProjectWorktreeInventory),
  managedWorktrees: Schema.Array(ManagedThreadWorktree),
  providers: Schema.Array(CommandLineDiscoveryProvider),
});
export type CommandLineDiscoverySnapshot = typeof CommandLineDiscoverySnapshot.Type;

export const CommandLineWorktreeIdentity = Schema.Union([
  Schema.Struct({ type: Schema.Literal("primary-checkout"), projectId: ProjectId }),
  Schema.Struct({ type: Schema.Literal("existing-worktree"), worktreeId: TrimmedNonEmptyString }),
  Schema.Struct({
    type: Schema.Literal("managed-thread-worktree"),
    worktreeId: ManagedThreadWorktreeId,
  }),
]);
export type CommandLineWorktreeIdentity = typeof CommandLineWorktreeIdentity.Type;

export const CommandLineWorktreeSummary = Schema.Struct({
  identity: CommandLineWorktreeIdentity,
  owningProject: Schema.Struct({ projectId: ProjectId, title: TrimmedNonEmptyString }),
  classification: Schema.Literals(["primary", "existing", "managed"]),
  path: TrimmedNonEmptyString,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  name: Schema.NullOr(TrimmedNonEmptyString),
  availability: Schema.Literals(["available", "in-use", "missing", "unavailable"]),
  eligibleForThread: Schema.Boolean,
  threadIds: Schema.Array(ThreadId),
  authoritativeRevision: Schema.optional(IsoDateTime),
});
export type CommandLineWorktreeSummary = typeof CommandLineWorktreeSummary.Type;

export const CommandLineTruncation = Schema.Struct({ omittedRecords: NonNegativeInt });

export const CommandLineWorktreeListData = Schema.Struct({
  type: Schema.Literal("worktree-list"),
  worktrees: Schema.Array(CommandLineWorktreeSummary),
  truncation: CommandLineTruncation,
});
export type CommandLineWorktreeListData = typeof CommandLineWorktreeListData.Type;

export const CommandLineWorktreeDeleteData = Schema.Struct({
  type: Schema.Literal("worktree-delete"),
  disposition: Schema.Literals(["deleted", "already-resolved"]),
  identity: Schema.Struct({ worktreeId: ManagedThreadWorktreeId }),
  project: Schema.Struct({ projectId: ProjectId }),
  commandIdentity: Schema.Struct({
    commandId: CommandId,
    normalizedInputDigest: EnvironmentCommandNormalizedInputDigest,
  }),
  acceptedSequence: NonNegativeInt,
  previousRevision: IsoDateTime,
  authoritativeRevision: IsoDateTime,
  previousState: Schema.Literals(["present", "missing"]),
  associatedThreadIds: Schema.Array(ThreadId),
  associatedThreadsDeleted: Schema.Boolean,
  primaryCheckoutTargeted: Schema.Literal(false),
  externalWorktreeTargeted: Schema.Literal(false),
  branchIntegrated: Schema.Literal(false),
  clientReadinessGeneration: TrimmedNonEmptyString,
});
export type CommandLineWorktreeDeleteData = typeof CommandLineWorktreeDeleteData.Type;

export const CommandLineEffortOption = Schema.Struct({
  value: TrimmedNonEmptyString,
  label: TrimmedNonEmptyString,
  isDefault: Schema.Boolean,
});

export const CommandLineProviderModelOption = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  effortOptionId: Schema.NullOr(TrimmedNonEmptyString),
  efforts: Schema.Array(CommandLineEffortOption),
});

export const CommandLineProviderOption = Schema.Struct({
  identity: Schema.Struct({ instanceId: ProviderInstanceId }),
  driver: ProviderDriverKind,
  displayName: TrimmedNonEmptyString,
  models: Schema.Array(CommandLineProviderModelOption),
});
export type CommandLineProviderOption = typeof CommandLineProviderOption.Type;

export const CommandLineExistingWorktreeChoice = Schema.Struct({
  identity: Schema.Union([
    Schema.Struct({
      type: Schema.Literal("existing-worktree"),
      worktreeId: TrimmedNonEmptyString,
    }),
    Schema.Struct({
      type: Schema.Literal("managed-thread-worktree"),
      worktreeId: ManagedThreadWorktreeId,
    }),
  ]),
  path: TrimmedNonEmptyString,
  branch: Schema.NullOr(TrimmedNonEmptyString),
  name: Schema.NullOr(TrimmedNonEmptyString),
  classification: Schema.Literals(["existing", "managed"]),
  availability: Schema.Literals(["available", "in-use", "missing", "unavailable"]),
});

export const CommandLineThreadOptionsData = Schema.Struct({
  type: Schema.Literal("thread-options"),
  authoritativeRevision: NonNegativeInt,
  project: CommandLineProjectSummary,
  resolvedBy: Schema.Literals(["project-id", "worktree-id", "explicit-path", "checkout-inference"]),
  providers: Schema.Array(CommandLineProviderOption),
  projectDefaults: Schema.NullOr(ModelSelection),
  checkoutChoices: Schema.Struct({
    primary: Schema.Struct({
      identity: Schema.Struct({ type: Schema.Literal("primary-checkout"), projectId: ProjectId }),
      path: TrimmedNonEmptyString,
    }),
    newWorktrees: Schema.Array(
      Schema.Struct({
        id: TrimmedNonEmptyString,
        location: ThreadWorktreeLocation,
        label: TrimmedNonEmptyString,
      }),
    ),
    existingWorktrees: Schema.Array(CommandLineExistingWorktreeChoice),
  }),
  truncation: CommandLineTruncation,
});
export type CommandLineThreadOptionsData = typeof CommandLineThreadOptionsData.Type;

export const CommandLineDiscoveryCommand = Schema.Literals([
  "worktree.list",
  "worktree.delete",
  "thread.options",
]);
export type CommandLineDiscoveryCommand = typeof CommandLineDiscoveryCommand.Type;

export const CommandLineDiscoveryFailureCode = Schema.Literals([
  "invalid-input",
  "project-not-found",
  "project-ambiguous",
  "worktree-unavailable",
  "worktree-not-found",
  "revision-conflict",
  "worktree-busy",
  "command-rejected",
  "environment-stopped",
  "environment-unavailable",
  "provider-field-limit-exceeded",
  "result-limit-exceeded",
  "operational-failure",
]);
export type CommandLineDiscoveryFailureCode = typeof CommandLineDiscoveryFailureCode.Type;

export const CommandLineDiscoveryError = Schema.Struct({
  code: CommandLineDiscoveryFailureCode,
  message: CommandLineSafeDiagnostic,
  authoritativeRevision: Schema.optional(IsoDateTime),
});
export type CommandLineDiscoveryError = typeof CommandLineDiscoveryError.Type;

const CommandLineDiscoveryResultMetadata = {
  recordType: Schema.Literal("discovery-command-result"),
  applicationRelease: CommandLineApplicationReleaseIdentity,
} as const;

export const CommandLineDiscoverySuccessResult = Schema.Union([
  Schema.Struct({
    ...CommandLineDiscoveryResultMetadata,
    command: Schema.Literal("worktree.list"),
    outcome: Schema.Literal("success"),
    data: CommandLineWorktreeListData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineDiscoveryResultMetadata,
    command: Schema.Literal("worktree.delete"),
    outcome: Schema.Literal("success"),
    data: CommandLineWorktreeDeleteData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineDiscoveryResultMetadata,
    command: Schema.Literal("thread.options"),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadOptionsData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
]);
export type CommandLineDiscoverySuccessResult = typeof CommandLineDiscoverySuccessResult.Type;

const commandLineDiscoveryFailureResult = <Code extends CommandLineDiscoveryFailureCode>(
  code: Code,
) =>
  Schema.Struct({
    ...CommandLineDiscoveryResultMetadata,
    command: CommandLineDiscoveryCommand,
    outcome: Schema.Literal(code),
    data: Schema.Null,
    error: Schema.Struct({
      code: Schema.Literal(code),
      message: CommandLineSafeDiagnostic,
      authoritativeRevision: Schema.optional(Schema.Union([NonNegativeInt, IsoDateTime])),
    }),
    environment: Schema.NullOr(CommandLineEnvironmentIdentity),
  });

export const CommandLineDiscoveryFailureResult = Schema.Union([
  commandLineDiscoveryFailureResult("invalid-input"),
  commandLineDiscoveryFailureResult("project-not-found"),
  commandLineDiscoveryFailureResult("project-ambiguous"),
  commandLineDiscoveryFailureResult("worktree-unavailable"),
  commandLineDiscoveryFailureResult("worktree-not-found"),
  commandLineDiscoveryFailureResult("revision-conflict"),
  commandLineDiscoveryFailureResult("worktree-busy"),
  commandLineDiscoveryFailureResult("command-rejected"),
  commandLineDiscoveryFailureResult("environment-stopped"),
  commandLineDiscoveryFailureResult("environment-unavailable"),
  commandLineDiscoveryFailureResult("provider-field-limit-exceeded"),
  commandLineDiscoveryFailureResult("result-limit-exceeded"),
  commandLineDiscoveryFailureResult("operational-failure"),
]);
export type CommandLineDiscoveryFailureResult = typeof CommandLineDiscoveryFailureResult.Type;

export const CommandLineDiscoveryResult = Schema.Union([
  CommandLineDiscoverySuccessResult,
  CommandLineDiscoveryFailureResult,
]);
export type CommandLineDiscoveryResult = typeof CommandLineDiscoveryResult.Type;

export const COMMAND_LINE_MESSAGE_VIEW_MAX_RECORDS = 2_000;

export const CommandLineThreadOutputView = Schema.Literals([
  "final",
  "messages",
  "tasks",
  "plan",
  "diff",
  "activities",
]);
export type CommandLineThreadOutputView = typeof CommandLineThreadOutputView.Type;

/**
 * Whether an output view covers the whole Persistent Provider Thread when the caller names no
 * turn. The conversational views do; `final` and `diff` describe one turn by nature and fall back
 * to the latest one. Every layer that narrows output by turn asks this, so the rule is stated
 * once here rather than re-derived per layer.
 */
export const commandLineThreadOutputViewSpansThread = (
  view: CommandLineThreadOutputView | undefined,
): boolean => view !== "final" && view !== "diff";

export const CommandLineThreadExecutionPhase = Schema.Literals([
  "provider-running",
  "idle",
  "queued",
  "starting",
  "running",
  "interrupted",
  "stopped",
  "failed",
]);
export type CommandLineThreadExecutionPhase = typeof CommandLineThreadExecutionPhase.Type;

export const CommandLineThreadAttention = Schema.Struct({
  id: Schema.NullOr(TrimmedNonEmptyString),
  type: Schema.Literals([
    "approval-required",
    "user-input-required",
    "actionable-plan",
    "unresolved-thread-work",
    "approval-expired",
    "user-input-expired",
  ]),
  count: NonNegativeInt,
  blocking: Schema.Boolean,
  turnId: Schema.NullOr(TurnId),
  revision: NonNegativeInt,
});
export type CommandLineThreadAttention = typeof CommandLineThreadAttention.Type;

export const CommandLineAcceptedWorkIdentity = EnvironmentCommandAcceptedWorkIdentity;
export type CommandLineAcceptedWorkIdentity = typeof CommandLineAcceptedWorkIdentity.Type;

export const CommandLineThreadSourceSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(CommandLineDiscoveryProject),
  activeThreads: Schema.Array(OrchestrationThreadShell),
  archivedThreads: Schema.Array(OrchestrationThreadShell),
  inventories: Schema.Array(CommandLineProjectWorktreeInventory),
  managedWorktrees: Schema.Array(ManagedThreadWorktree),
  thread: Schema.optional(Schema.NullOr(OrchestrationThread)),
  deletedThreadIds: Schema.optional(Schema.Array(ThreadId)),
  attention: Schema.optional(Schema.Array(CommandLineThreadAttention)),
  acceptedWork: Schema.optional(CommandLineAcceptedWorkIdentity),
  sourceOmittedRecords: Schema.optional(NonNegativeInt),
  sourceOmittedBytes: Schema.optional(NonNegativeInt),
  providerFieldLimitExceeded: Schema.optional(Schema.Boolean),
  sourceLimitExceeded: Schema.optional(Schema.Boolean),
  contextBinding: Schema.optional(Schema.NullOr(ThreadContextBinding)),
  selectedTurnExists: Schema.optional(Schema.Boolean),
  diff: Schema.optional(Schema.String),
});
export type CommandLineThreadSourceSnapshot = typeof CommandLineThreadSourceSnapshot.Type;

export const CommandLineThreadSummary = Schema.Struct({
  identity: Schema.Struct({ threadId: ThreadId }),
  title: TrimmedNonEmptyString,
  project: Schema.Struct({ projectId: ProjectId, title: TrimmedNonEmptyString }),
  management: Schema.optional(Schema.NullOr(ThreadManagement)),
  lifecycle: Schema.Literals(["active", "archived"]),
  provider: Schema.Struct({
    instanceId: ProviderInstanceId,
    model: TrimmedNonEmptyString,
  }),
  executionPhase: CommandLineThreadExecutionPhase,
  updatedAt: IsoDateTime,
});
export type CommandLineThreadSummary = typeof CommandLineThreadSummary.Type;

export const CommandLineThreadListData = Schema.Struct({
  type: Schema.Literal("thread-list"),
  threads: Schema.Array(CommandLineThreadSummary),
  truncation: CommandLineTruncation,
});
export type CommandLineThreadListData = typeof CommandLineThreadListData.Type;

export const CommandLineThreadCheckoutBinding = Schema.Struct({
  identity: CommandLineWorktreeIdentity,
  path: TrimmedNonEmptyString,
  branch: Schema.NullOr(TrimmedNonEmptyString),
});

export const CommandLineThreadShowData = Schema.Struct({
  providerGoal: Schema.optional(Schema.NullOr(ProviderGoalSnapshot)),
  type: Schema.Literal("thread-show"),
  identity: Schema.Struct({ threadId: ThreadId }),
  title: TrimmedNonEmptyString,
  management: Schema.optional(Schema.NullOr(ThreadManagement)),
  lifecycle: Schema.Literals(["active", "archived"]),
  project: CommandLineProjectSummary,
  provider: Schema.Struct({
    instanceId: ProviderInstanceId,
    model: TrimmedNonEmptyString,
  }),
  contextBinding: Schema.Struct({
    projectId: ProjectId,
    checkout: CommandLineThreadCheckoutBinding,
    binding: Schema.NullOr(ThreadContextBinding),
  }),
  authoritativeRevision: NonNegativeInt,
});
export type CommandLineThreadShowData = typeof CommandLineThreadShowData.Type;

export const CommandLineThreadStatusData = Schema.Struct({
  type: Schema.Literal("thread-status"),
  identity: Schema.Struct({ threadId: ThreadId }),
  clientReadiness: Schema.Literals(["unavailable", "starting", "synchronizing", "ready"]),
  executionPhase: CommandLineThreadExecutionPhase,
  attention: Schema.Array(CommandLineThreadAttention),
  activeTurnId: Schema.NullOr(TurnId),
  latestTurnId: Schema.NullOr(TurnId),
  durableSequence: NonNegativeInt,
  clientReadinessGeneration: TrimmedNonEmptyString,
});
export type CommandLineThreadStatusData = typeof CommandLineThreadStatusData.Type;

export const CommandLineThreadControlData = Schema.Struct({
  type: Schema.Literal("thread-control"),
  identity: Schema.Struct({ threadId: ThreadId }),
  action: Schema.Literals(["interrupt", "stop"]),
  disposition: Schema.Literals(["interrupt-requested", "stop-requested", "already-resolved"]),
  targetTurnId: Schema.NullOr(TurnId),
  terminalOutcome: Schema.NullOr(Schema.Literals(["completed", "failed", "interrupted"])),
  queueRevision: NonNegativeInt,
  pendingDepth: NonNegativeInt,
  acceptedSequence: Schema.NullOr(NonNegativeInt),
  clientReadinessGeneration: TrimmedNonEmptyString,
});
export type CommandLineThreadControlData = typeof CommandLineThreadControlData.Type;

export const CommandLineThreadAdministrationData = Schema.Struct({
  type: Schema.Literal("thread-administration"),
  action: Schema.Literals(["fork", "revert", "archive", "unarchive", "delete"]),
  disposition: Schema.Literals(["accepted", "already-resolved"]),
  identity: Schema.Struct({ threadId: ThreadId }),
  targetThreadId: Schema.NullOr(ThreadId),
  selectedTurnId: Schema.NullOr(TurnId),
  selectedMessageId: Schema.NullOr(MessageId),
  lifecycle: Schema.NullOr(Schema.Literals(["active", "archived", "deleted"])),
  commandIdentity: Schema.Struct({
    commandId: CommandId,
    normalizedInputDigest: EnvironmentCommandNormalizedInputDigest,
  }),
  previousRevision: IsoDateTime,
  authoritativeRevision: Schema.Union([NonNegativeInt, IsoDateTime]),
  acceptedSequence: Schema.NullOr(NonNegativeInt),
  sourceThreadMutated: Schema.Boolean,
  filesystemRestored: Schema.Literal(false),
  threadWorktreeDeleted: Schema.Literal(false),
  clientReadinessGeneration: TrimmedNonEmptyString,
});
export type CommandLineThreadAdministrationData = typeof CommandLineThreadAdministrationData.Type;

export const CommandLineThreadAttentionRequestStatus = Schema.Literals([
  "unresolved",
  "resolved",
  "expired",
]);

const CommandLineThreadAttentionListBase = {
  identity: Schema.Struct({ threadId: ThreadId }),
  executionPhase: CommandLineThreadExecutionPhase,
  revision: NonNegativeInt,
  durableSequence: NonNegativeInt,
  clientReadinessGeneration: TrimmedNonEmptyString,
  truncation: CommandLineTruncation,
} as const;

const CommandLineThreadAttentionRequestBase = {
  requestId: TrimmedNonEmptyString,
  expectedRevision: NonNegativeInt,
  status: CommandLineThreadAttentionRequestStatus,
  blocking: Schema.Boolean,
  callbackViable: Schema.Boolean,
  turnId: Schema.NullOr(TurnId),
  requestedAt: IsoDateTime,
  resolvedAt: Schema.NullOr(IsoDateTime),
} as const;

export const CommandLineThreadApprovalRequest = Schema.Struct({
  ...CommandLineThreadAttentionRequestBase,
  requestKind: Schema.NullOr(ProviderRequestKind),
  resolution: Schema.NullOr(ProviderApprovalDecision),
});
export type CommandLineThreadApprovalRequest = typeof CommandLineThreadApprovalRequest.Type;

export const CommandLineThreadApprovalListData = Schema.Struct({
  type: Schema.Literal("thread-approval-list"),
  ...CommandLineThreadAttentionListBase,
  requests: Schema.Array(CommandLineThreadApprovalRequest),
});
export type CommandLineThreadApprovalListData = typeof CommandLineThreadApprovalListData.Type;

export const CommandLineThreadInputQuestion = Schema.Struct({
  questionId: TrimmedNonEmptyString,
  prompt: TrimmedNonEmptyString,
  options: Schema.Array(TrimmedNonEmptyString),
  allowsMultiple: Schema.Boolean,
});
export type CommandLineThreadInputQuestion = typeof CommandLineThreadInputQuestion.Type;

export const CommandLineThreadInputRequest = Schema.Struct({
  ...CommandLineThreadAttentionRequestBase,
  questions: Schema.Array(CommandLineThreadInputQuestion),
  resolution: Schema.NullOr(
    Schema.Struct({ answeredQuestionIds: Schema.Array(TrimmedNonEmptyString) }),
  ),
});
export type CommandLineThreadInputRequest = typeof CommandLineThreadInputRequest.Type;

export const CommandLineThreadInputListData = Schema.Struct({
  type: Schema.Literal("thread-input-list"),
  ...CommandLineThreadAttentionListBase,
  requests: Schema.Array(CommandLineThreadInputRequest),
});
export type CommandLineThreadInputListData = typeof CommandLineThreadInputListData.Type;

export const CommandLineThreadInputAnswer = Schema.Struct({
  questionId: TrimmedNonEmptyString,
  values: Schema.Array(TrimmedNonEmptyString),
});
export type CommandLineThreadInputAnswer = typeof CommandLineThreadInputAnswer.Type;

export const CommandLineThreadInputAnswers = Schema.Array(CommandLineThreadInputAnswer);
export type CommandLineThreadInputAnswers = typeof CommandLineThreadInputAnswers.Type;

export const CommandLineThreadAttentionResolutionData = Schema.Struct({
  type: Schema.Literal("thread-attention-resolution"),
  identity: Schema.Struct({ threadId: ThreadId }),
  attentionType: Schema.Literals(["approval", "user-input"]),
  requestId: TrimmedNonEmptyString,
  disposition: Schema.Literals(["response-authorized", "already-authoritative"]),
  decision: Schema.NullOr(ProviderApprovalDecision),
  answeredQuestionIds: Schema.Array(TrimmedNonEmptyString),
  turnId: Schema.NullOr(TurnId),
  previousRevision: NonNegativeInt,
  authoritativeRevision: NonNegativeInt,
  acceptedSequence: Schema.NullOr(NonNegativeInt),
  clientReadinessGeneration: TrimmedNonEmptyString,
});
export type CommandLineThreadAttentionResolutionData =
  typeof CommandLineThreadAttentionResolutionData.Type;

const CommandLineOutputMessage = Schema.Struct({
  type: Schema.Literal("message"),
  id: MessageId,
  role: Schema.Literals(["user", "assistant", "system"]),
  text: Schema.String,
  turnId: Schema.NullOr(TurnId),
  incomplete: Schema.Boolean,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

const CommandLineOutputTask = Schema.Struct({
  type: Schema.Literal("task"),
  id: EventId,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});

const CommandLineOutputPlan = Schema.Struct({
  type: Schema.Literal("plan"),
  id: TrimmedNonEmptyString,
  turnId: Schema.NullOr(TurnId),
  markdown: TrimmedNonEmptyString,
  implemented: Schema.Boolean,
  updatedAt: IsoDateTime,
});

const CommandLineOutputDiff = Schema.Struct({
  type: Schema.Literal("diff"),
  turnId: TurnId,
  patch: Schema.String,
});

const CommandLineOutputActivity = Schema.Struct({
  type: Schema.Literal("activity"),
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});

export const CommandLineThreadOutputRecord = Schema.Union([
  CommandLineOutputMessage,
  CommandLineOutputTask,
  CommandLineOutputPlan,
  CommandLineOutputDiff,
  CommandLineOutputActivity,
]);
export type CommandLineThreadOutputRecord = typeof CommandLineThreadOutputRecord.Type;

export const CommandLineThreadOutputData = Schema.Struct({
  type: Schema.Literal("thread-output"),
  identity: Schema.Struct({ threadId: ThreadId }),
  turnId: Schema.NullOr(TurnId),
  view: CommandLineThreadOutputView,
  incomplete: Schema.Boolean,
  records: Schema.Array(CommandLineThreadOutputRecord),
  truncation: Schema.Struct({
    omittedRecords: NonNegativeInt,
    omittedBytes: NonNegativeInt,
  }),
});
export type CommandLineThreadOutputData = typeof CommandLineThreadOutputData.Type;

export const CommandLineThreadCommand = Schema.Literals([
  "thread.list",
  "thread.show",
  "thread.status",
  "thread.output",
  "thread.interrupt",
  "thread.stop",
  "thread.fork",
  "thread.revert",
  "thread.archive",
  "thread.unarchive",
  "thread.delete",
  "thread.approval.list",
  "thread.approval.respond",
  "thread.input.list",
  "thread.input.respond",
]);
export type CommandLineThreadCommand = typeof CommandLineThreadCommand.Type;

export const CommandLineThreadFailureCode = Schema.Literals([
  "invalid-input",
  "thread-not-found",
  "thread-deleted",
  "turn-not-found",
  "turn-not-completed",
  "message-not-found",
  "thread-not-idle",
  "queue-entry-not-found",
  "queue-entry-not-pending",
  "revision-conflict",
  "active-turn-not-found",
  "active-turn-replaced",
  "attention-request-not-found",
  "attention-request-expired",
  "command-rejected",
  "command-outcome-unknown",
  "environment-stopped",
  "environment-unavailable",
  "provider-field-limit-exceeded",
  "result-limit-exceeded",
  "operational-failure",
]);
export type CommandLineThreadFailureCode = typeof CommandLineThreadFailureCode.Type;

const CommandLineThreadResultMetadata = {
  recordType: Schema.Literal("thread-command-result"),
  applicationRelease: CommandLineApplicationReleaseIdentity,
} as const;

export const CommandLineThreadSuccessResult = Schema.Union([
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literal("thread.list"),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadListData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literal("thread.show"),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadShowData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literal("thread.status"),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadStatusData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literal("thread.output"),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadOutputData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literals(["thread.interrupt", "thread.stop"]),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadControlData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literals(["thread.approval.list", "thread.input.list"]),
    outcome: Schema.Literal("success"),
    data: Schema.Union([CommandLineThreadApprovalListData, CommandLineThreadInputListData]),
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literals(["thread.approval.respond", "thread.input.respond"]),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadAttentionResolutionData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: Schema.Literals([
      "thread.fork",
      "thread.revert",
      "thread.archive",
      "thread.unarchive",
      "thread.delete",
    ]),
    outcome: Schema.Literal("success"),
    data: CommandLineThreadAdministrationData,
    error: Schema.Null,
    environment: CommandLineEnvironmentIdentity,
  }),
]);
export type CommandLineThreadSuccessResult = typeof CommandLineThreadSuccessResult.Type;

export const CommandLineThreadNoOutputResult = Schema.Struct({
  ...CommandLineThreadResultMetadata,
  command: Schema.Literal("thread.output"),
  outcome: Schema.Literal("no-output"),
  data: Schema.Null,
  error: Schema.Null,
  environment: CommandLineEnvironmentIdentity,
});

const commandLineThreadFailureResult = <Code extends CommandLineThreadFailureCode>(code: Code) =>
  Schema.Struct({
    ...CommandLineThreadResultMetadata,
    command: CommandLineThreadCommand,
    outcome: Schema.Literal(code),
    data: Schema.Null,
    error: Schema.Struct({
      code: Schema.Literal(code),
      message: CommandLineSafeDiagnostic,
      authoritativeRevision: Schema.optional(Schema.Union([NonNegativeInt, IsoDateTime])),
    }),
    environment: Schema.NullOr(CommandLineEnvironmentIdentity),
  });

export const CommandLineThreadFailureResult = Schema.Union([
  commandLineThreadFailureResult("invalid-input"),
  commandLineThreadFailureResult("thread-not-found"),
  commandLineThreadFailureResult("thread-deleted"),
  commandLineThreadFailureResult("turn-not-found"),
  commandLineThreadFailureResult("turn-not-completed"),
  commandLineThreadFailureResult("message-not-found"),
  commandLineThreadFailureResult("thread-not-idle"),
  commandLineThreadFailureResult("queue-entry-not-found"),
  commandLineThreadFailureResult("queue-entry-not-pending"),
  commandLineThreadFailureResult("revision-conflict"),
  commandLineThreadFailureResult("active-turn-not-found"),
  commandLineThreadFailureResult("active-turn-replaced"),
  commandLineThreadFailureResult("attention-request-not-found"),
  commandLineThreadFailureResult("attention-request-expired"),
  commandLineThreadFailureResult("command-rejected"),
  commandLineThreadFailureResult("command-outcome-unknown"),
  commandLineThreadFailureResult("environment-stopped"),
  commandLineThreadFailureResult("environment-unavailable"),
  commandLineThreadFailureResult("provider-field-limit-exceeded"),
  commandLineThreadFailureResult("result-limit-exceeded"),
  commandLineThreadFailureResult("operational-failure"),
]);
export type CommandLineThreadFailureResult = typeof CommandLineThreadFailureResult.Type;

export const CommandLineThreadResult = Schema.Union([
  CommandLineThreadSuccessResult,
  CommandLineThreadNoOutputResult,
  CommandLineThreadFailureResult,
]);
export type CommandLineThreadResult = typeof CommandLineThreadResult.Type;

export const COMMAND_LINE_PROMPT_MAX_BYTES = 1024 * 1024;
export const COMMAND_LINE_PROMPT_REQUEST_MAX_BYTES = 2 * 1024 * 1024;
export const COMMAND_LINE_STREAM_CATCH_UP_MAX_RECORDS = 10_000;
export const COMMAND_LINE_STREAM_PENDING_OUTPUT_MAX_BYTES = 4 * 1024 * 1024;
export const COMMAND_LINE_STREAM_RECORD_MAX_BYTES = 1024 * 1024;
export const COMMAND_LINE_STREAM_SNAPSHOT_MAX_BYTES = 16 * 1024 * 1024;
export const COMMAND_LINE_STREAM_SYNCHRONIZATION_TIMEOUT_MS = 30_000;
export const COMMAND_LINE_STREAM_RECONNECT_COOLDOWN_MS = 250;
export const COMMAND_LINE_STREAM_RECONNECT_MAX_ATTEMPTS = 8;
export const COMMAND_LINE_CONNECTION_READ_TIMEOUT_MS = 30_000;
export const COMMAND_LINE_DISPATCH_MAX_ATTEMPTS = 5;
export const COMMAND_LINE_ACCEPTED_WORK_RECOVERY_MAX_ATTEMPTS = 120;
export const COMMAND_LINE_DISPATCH_BACKOFF_MS = 250;
export const COMMAND_LINE_DISPATCH_DEADLINE_MS = 30_000;
export const COMMAND_LINE_ACCEPTED_WORK_RECOVERY_DEADLINE_MS = 30_000;
export const COMMAND_LINE_LIFECYCLE_STATUS_TIMEOUT_MS = 5_000;
export const COMMAND_LINE_LIFECYCLE_START_TIMEOUT_MS = 60_000;
export const COMMAND_LINE_LIFECYCLE_STOP_TIMEOUT_MS = 30_000;

const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).byteLength;

export const CommandLinePromptText = Schema.String.check(
  Schema.makeFilter(
    (value) =>
      (value.trim().length > 0 && utf8ByteLength(value) <= COMMAND_LINE_PROMPT_MAX_BYTES) ||
      `Prompt text must be non-empty and no larger than ${COMMAND_LINE_PROMPT_MAX_BYTES} UTF-8 bytes.`,
    { identifier: "CommandLinePromptText" },
  ),
);

export const CommandLineNewThreadRequest = Schema.Struct({
  projectId: ProjectId,
  optionsRevision: NonNegativeInt,
  providerInstanceId: ProviderInstanceId,
  modelId: TrimmedNonEmptyString,
  effort: Schema.optional(TrimmedNonEmptyString),
  checkoutId: TrimmedNonEmptyString,
  baseBranch: Schema.optional(TrimmedNonEmptyString),
  newBranch: Schema.optional(TrimmedNonEmptyString),
  title: Schema.optional(TrimmedNonEmptyString),
});
export type CommandLineNewThreadRequest = typeof CommandLineNewThreadRequest.Type;

export const CommandLineThreadPromptRequest = Schema.Struct({
  command: Schema.Literal("thread.prompt"),
  prompt: CommandLinePromptText,
  commandId: Schema.optional(CommandId),
  messageId: Schema.optional(MessageId),
  threadId: Schema.optional(ThreadId),
  createdAt: Schema.optional(IsoDateTime),
  newThread: Schema.optional(CommandLineNewThreadRequest),
});
export type CommandLineThreadPromptRequest = typeof CommandLineThreadPromptRequest.Type;

const CommandLinePromptReceiptMetadata = {
  recordType: Schema.Literal("thread-prompt-receipt"),
  command: Schema.Literal("thread.prompt"),
  applicationRelease: CommandLineApplicationReleaseIdentity,
  environment: CommandLineEnvironmentIdentity,
  commandIdentity: Schema.Struct({ commandId: CommandId, messageId: MessageId }),
  thread: Schema.Struct({ threadId: ThreadId }),
  acceptedSequence: NonNegativeInt,
  normalizedInputDigest: EnvironmentCommandNormalizedInputDigest,
  createdThread: Schema.optional(
    Schema.Struct({
      project: Schema.Struct({ projectId: ProjectId }),
      provider: Schema.Struct({
        instanceId: ProviderInstanceId,
        model: TrimmedNonEmptyString,
      }),
      checkout: CommandLineThreadCheckoutBinding,
    }),
  ),
} as const;

export const CommandLineThreadPromptReceipt = Schema.Union([
  Schema.Struct({
    ...CommandLinePromptReceiptMetadata,
    disposition: Schema.Literal("started"),
    acceptedWork: Schema.Struct({
      type: Schema.Literal("direct-turn"),
      messageId: MessageId,
      turnId: Schema.NullOr(TurnId),
    }),
  }),
]);
export type CommandLineThreadPromptReceipt = typeof CommandLineThreadPromptReceipt.Type;

export const CommandLineThreadBoundaryOutcome = Schema.Literals([
  "completed",
  "failed",
  "interrupted",
  "attention-required",
  "wait-expired",
  "client-interrupted",
  "target-disappeared",
  "continuity-lost",
]);
export type CommandLineThreadBoundaryOutcome = typeof CommandLineThreadBoundaryOutcome.Type;

export const CommandLineThreadBoundary = Schema.Struct({
  recordType: Schema.Literal("thread-command-boundary"),
  command: Schema.Literals(["thread.prompt", "thread.watch"]),
  outcome: CommandLineThreadBoundaryOutcome,
  thread: Schema.Struct({ threadId: ThreadId }),
  acceptedWork: Schema.NullOr(CommandLineAcceptedWorkIdentity),
  lastStreamSequence: NonNegativeInt,
  lastSourceSequence: Schema.NullOr(NonNegativeInt),
  detail: CommandLineSafeDiagnostic,
  receipt: Schema.optional(CommandLineThreadPromptReceipt),
});
export type CommandLineThreadBoundary = typeof CommandLineThreadBoundary.Type;

export const CommandLineThreadObservationEvent = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("snapshot"),
    snapshotSequence: NonNegativeInt,
    executionPhase: CommandLineThreadExecutionPhase,
    activeTurnId: Schema.NullOr(TurnId),
    latestTurnId: Schema.NullOr(TurnId),
  }),
  Schema.Struct({
    type: Schema.Literal("ready"),
    baselineSequence: Schema.NullOr(NonNegativeInt),
  }),
  Schema.Struct({
    type: Schema.Literal("message"),
    messageId: MessageId,
    role: Schema.Literals(["user", "assistant", "system"]),
    text: Schema.String,
    turnId: Schema.NullOr(TurnId),
    incomplete: Schema.Boolean,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("activity"),
    activityId: EventId,
    tone: OrchestrationThreadActivityTone,
    kind: TrimmedNonEmptyString,
    summary: TrimmedNonEmptyString,
    turnId: Schema.NullOr(TurnId),
    occurredAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("attention"),
    attentionType: Schema.Literals(["approval", "user-input"]),
    outcome: Schema.Literals(["requested", "resolved", "expired"]),
    requestId: TrimmedNonEmptyString,
    blocking: Schema.Boolean,
    callbackViable: Schema.Boolean,
    turnId: Schema.NullOr(TurnId),
    resolution: Schema.NullOr(ProviderApprovalDecision),
    occurredAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("turn"),
    turnId: Schema.NullOr(TurnId),
    status: Schema.Literals([
      "idle",
      "starting",
      "running",
      "ready",
      "interrupted",
      "stopped",
      "error",
    ]),
    occurredAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("event"),
    eventId: EventId,
    eventType: TrimmedNonEmptyString,
    occurredAt: IsoDateTime,
  }),
  Schema.Struct({
    type: Schema.Literal("reset"),
    reason: Schema.Literals([
      "generation-changed",
      "continuity-unavailable",
      "sequence-gap",
      "backpressure-overflow",
    ]),
    previousGeneration: TrimmedNonEmptyString,
    nextGeneration: TrimmedNonEmptyString,
  }),
]);
export type CommandLineThreadObservationEvent = typeof CommandLineThreadObservationEvent.Type;

export const CommandLineThreadObservationRecord = Schema.Struct({
  recordType: Schema.Literal("thread-observation"),
  command: Schema.Literals(["thread.prompt", "thread.watch"]),
  phase: Schema.Literals(["snapshot", "catch-up", "ready", "live", "replacement"]),
  streamSequence: NonNegativeInt,
  sourceSequence: Schema.NullOr(NonNegativeInt),
  readinessGeneration: TrimmedNonEmptyString,
  thread: Schema.Struct({ threadId: ThreadId }),
  event: CommandLineThreadObservationEvent,
});
export type CommandLineThreadObservationRecord = typeof CommandLineThreadObservationRecord.Type;
